import {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  FC,
  useCallback,
  useState,
  MutableRefObject,
  useRef,
} from "react";
import { LaserScanDataModel } from "../types/laserScanScene";

type LaserScanSceneContextType = {
  isDisplaySwitchDisabled: boolean;
  isDisplayIRShell: boolean;
  heatData: LaserScanDataModel | undefined;

  isFirstRenderRef: MutableRefObject<boolean>;

  handleSwitchDisable: (isDisplay: boolean) => void;
  handleIRShellDisplay: (isDisplay: boolean) => void;
  handleHeatDataUpdate: (data: LaserScanDataModel) => void;
};

const initialContext: LaserScanSceneContextType = {
  isDisplaySwitchDisabled: false,
  isDisplayIRShell: true,
  heatData: undefined,
  isFirstRenderRef: { current: true },
  handleSwitchDisable: () => {},
  handleIRShellDisplay: () => {},
  handleHeatDataUpdate: () => {},
};

const LaserScanSceneContext =
  createContext<LaserScanSceneContextType>(initialContext);

type LaserScanSceneProviderProps = {
  children: ReactNode;
};

export const LaserScanSceneProvider: FC<LaserScanSceneProviderProps> = ({
  children,
}) => {
  const [isDisplaySwitchDisabled, setIsDisplaySwitchDisabled] =
    useState<boolean>(false);
  const [isDisplayIRShell, setIsDisplayIRShell] = useState<boolean>(false);
  const [heatData, setHeatData] = useState<LaserScanDataModel>();

  const isFirstRenderRef = useRef(true);

  const handleSwitchDisable = useCallback((isDisplay: boolean) => {
    setIsDisplaySwitchDisabled(isDisplay);
  }, []);

  const handleIRShellDisplay = useCallback((isDisplay: boolean) => {
    setIsDisplayIRShell(isDisplay);
  }, []);

  const handleHeatDataUpdate = useCallback((data: LaserScanDataModel) => {
    setHeatData(data);
  }, []);

  const resetScene = useCallback(() => {
    setIsDisplaySwitchDisabled(false);
    setIsDisplayIRShell(false);
    setHeatData(undefined);
  }, []);

  const contextValue = useMemo(
    () => ({
      isDisplaySwitchDisabled,
      isDisplayIRShell,
      heatData,
      isFirstRenderRef,
      handleSwitchDisable,
      handleIRShellDisplay,
      handleHeatDataUpdate,
      resetScene,
    }),
    [
      isDisplaySwitchDisabled,
      isDisplayIRShell,
      heatData,
      handleSwitchDisable,
      handleIRShellDisplay,
      handleHeatDataUpdate,
      resetScene,
    ],
  );

  return (
    <LaserScanSceneContext.Provider value={contextValue}>
      {children}
    </LaserScanSceneContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useLaserScanScene = (): LaserScanSceneContextType =>
  useContext(LaserScanSceneContext);

export default LaserScanSceneProvider;
